import React from 'react';
import Button from '@material-ui/core/Button';
import NotificationsIcon from '@material-ui/icons/Notifications';
import NotificationsOffIcon from '@material-ui/icons/NotificationsOff';
import { useMutation } from '@apollo/client';
import superUsers from '../../../../graph/superUsers';
import users from '../../../../graph/users';
import Tooltip from '../../../Tooltip';

interface NotificationsButtonProps {
  subscribedToNotifications: boolean;
  toggleSubscribedToNotifications: (value: boolean) => Promise<void>;
  labelYes?: string;
  labelNo?: string;
  tooltip?: string;
}

type SuperUserNotificationField =
  | 'dailyReportNotifications'
  | 'tooFewBraceletsNotifications'
  | 'gatewayOfflineEmailNotifications'
  | 'screenOfflineNotifications';

interface SuperUserFlagNotificationButtonProps {
  data?: {
    id?: number;
    dailyReportNotifications?: boolean;
    tooFewBraceletsNotifications?: boolean;
    gatewayOfflineEmailNotifications?: boolean;
    screenOfflineNotifications?: boolean;
  };
  field: SuperUserNotificationField;
  tooltip: string;
}

const NotificationsButton = ({
  toggleSubscribedToNotifications,
  subscribedToNotifications,
  labelYes,
  labelNo,
  tooltip,
}: NotificationsButtonProps) => {
  const toggle = async (e: any) => {
    e.stopPropagation();
    await toggleSubscribedToNotifications(subscribedToNotifications);
  };

  return (
    <Tooltip content={tooltip}>
      {subscribedToNotifications ? (
        <Button color="primary" onClick={toggle} startIcon={<NotificationsIcon />}>
          {labelYes || 'Subscribed to Notifications'}
        </Button>
      ) : (
        <Button color="primary" onClick={toggle} startIcon={<NotificationsOffIcon />}>
          {labelNo || 'Unsubscribed from Notifications'}
        </Button>
      )}
    </Tooltip>
  );
};

export const AdminNotificationsButton = (props: any) => {
  const [subscribeToNotifications] = useMutation(users.update);

  const toggleSubscribedToNotifications = async (val: boolean) => {
    await subscribeToNotifications({
      variables: { id: props.data?.id, subscribedToOpenPositionNotifications: !val },
    });
  };

  return (
    <NotificationsButton
      subscribedToNotifications={props.data?.subscribedToOpenPositionNotifications}
      toggleSubscribedToNotifications={toggleSubscribedToNotifications}
      labelYes={'Yes'}
      labelNo={'No'}
      tooltip={'Open position notifications'}
    />
  );
};

export const SensorEmailNotificationsButton = (props: any) => {
  const [subscribeToNotifications] = useMutation(users.update);

  const toggleSubscribedToNotifications = async (val: boolean) => {
    await subscribeToNotifications({
      variables: { id: props.data?.id, sensorEmailNotifications: !val },
    });
  };

  return (
    <NotificationsButton
      subscribedToNotifications={props.data?.sensorEmailNotifications}
      toggleSubscribedToNotifications={toggleSubscribedToNotifications}
      labelYes={'Yes'}
      labelNo={'No'}
      tooltip={'Sensor email notifications'}
    />
  );
};

export const SensorPhoneNotificationsButton = (props: any) => {
  const [subscribeToNotifications] = useMutation(users.update);

  const toggleSubscribedToNotifications = async (val: boolean) => {
    await subscribeToNotifications({
      variables: { id: props.data?.id, sensorPhoneNotifications: !val },
    });
  };

  return (
    <NotificationsButton
      subscribedToNotifications={props.data?.sensorPhoneNotifications}
      toggleSubscribedToNotifications={toggleSubscribedToNotifications}
      labelYes={'Yes'}
      labelNo={'No'}
      tooltip={'Sensor phone notifications'}
    />
  );
};

export const ReportDeliveryEmailButton = (props: any) => {
  const [mutate] = useMutation(users.update);

  const toggle = async (val: boolean) => {
    await mutate({
      variables: { id: props.data?.id, reportDeliveryEmail: !val },
    });
  };

  return (
    <NotificationsButton
      subscribedToNotifications={props.data?.reportDeliveryEmail ?? true}
      toggleSubscribedToNotifications={toggle}
      labelYes={'Yes'}
      labelNo={'No'}
      tooltip={'Report delivery via email'}
    />
  );
};

export const ReportDeliverySmsButton = (props: any) => {
  const [mutate] = useMutation(users.update);

  const toggle = async (val: boolean) => {
    await mutate({
      variables: { id: props.data?.id, reportDeliverySms: !val },
    });
  };

  return (
    <NotificationsButton
      subscribedToNotifications={props.data?.reportDeliverySms ?? true}
      toggleSubscribedToNotifications={toggle}
      labelYes={'Yes'}
      labelNo={'No'}
      tooltip={'Report delivery via SMS'}
    />
  );
};

const SuperUserFlagNotificationButton = ({ data, field, tooltip }: SuperUserFlagNotificationButtonProps) => {
  const [mutate] = useMutation(superUsers.update);

  const toggle = async (val: boolean) => {
    await mutate({
      variables: { id: data?.id, [field]: !val },
    });
  };

  return (
    <NotificationsButton
      subscribedToNotifications={Boolean(data?.[field])}
      toggleSubscribedToNotifications={toggle}
      labelYes={'Yes'}
      labelNo={'No'}
      tooltip={tooltip}
    />
  );
};

export const DailyReportNotificationButton = (props: any) => (
  <SuperUserFlagNotificationButton {...props} field="dailyReportNotifications" tooltip="Daily report notifications" />
);

export const TooFewBraceletsNotificationButton = (props: any) => (
  <SuperUserFlagNotificationButton
    {...props}
    field="tooFewBraceletsNotifications"
    tooltip="Too few bracelets notifications"
  />
);

export const GatewayOfflineEmailNotificationButton = (props: any) => (
  <SuperUserFlagNotificationButton
    {...props}
    field="gatewayOfflineEmailNotifications"
    tooltip="Gateway offline notifications"
  />
);

export const ScreenOfflineNotificationButton = (props: any) => (
  <SuperUserFlagNotificationButton
    {...props}
    field="screenOfflineNotifications"
    tooltip="Screen offline notifications"
  />
);

export default NotificationsButton;
